/**
 * CRM Pipeline Page
 *
 * Main pipeline page with Kanban board view
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useCrmPipelineBoard,
  useCrmFilters,
  useCreateOpportunity,
  useTransformToQuote,
  useMarkOpportunityLost,
  useLoadMoreForStage,
  crmPipelineKeys,
  crmActivityKeys,
} from '../hooks';
import {
  CrmPipelineBoard,
  CrmBoardHeader,
  CrmFilterBar,
  CrmBoardSkeleton,
} from '../components/board';
import { CreateOpportunityModal } from '../components/opportunity/CreateOpportunityModal';
import { TransformToQuoteModal } from '../components/opportunity/TransformToQuoteModal';
import { MarkLostModal } from '../components/opportunity/MarkLostModal';
import { AddActivityModal } from '../components/activity';
import { createActivity } from '../api/crmApi';
import { getErrorMessage } from '../utils/getErrorMessage';
import { PIPELINES } from '../constants';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { ExternalLink } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { PipelineOpportunity, PipelineStage, PipelineType, CreateCrmActivityInput, CreateOpportunityInput } from '../types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars

export function CrmPipelinePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pipeline tab — persisted to localStorage
  const [activePipeline, setActivePipeline] = useState<PipelineType>(() => {
    const saved = localStorage.getItem('atek-crm-pipeline-tab');
    return (saved === 'direct_sales' || saved === 'recurring_revenue' || saved === 'public_tender') ? saved : 'direct_sales';
  });
  const handlePipelineChange = (v: string) => {
    const pipeline = v as PipelineType;
    setActivePipeline(pipeline);
    localStorage.setItem('atek-crm-pipeline-tab', pipeline);
  };
  const pipelineDef = PIPELINES.find((p) => p.key === activePipeline) || PIPELINES[0]!;

  // Filters
  const { filters, setFilter, clearFilters, hasActiveFilters } = useCrmFilters();
  const [showFilters, setShowFilters] = useState(false);

  // Only pass pipeline filter for public_tender (new pipeline with its own stages)
  // direct_sales and recurring_revenue use legacy stages (prospect_acquired, etc.)
  const mergedFilters = activePipeline === 'public_tender'
    ? { ...filters, pipeline: 'public_tender' as const }
    : { ...filters };

  // Data fetching
  const { data: board, isLoading, error } = useCrmPipelineBoard(mergedFilters);

  // Mutations
  const createOpportunityMutation = useCreateOpportunity();
  const transformToQuoteMutation = useTransformToQuote();
  const markLostMutation = useMarkOpportunityLost();
  const loadMoreMutation = useLoadMoreForStage(mergedFilters);

  // Load more state - ref for synchronous guard, state for UI
  const loadingStageRef = useRef<string | null>(null);
  const [loadingStage, setLoadingStage] = useState<string | null>(null);

  // Detail panel
  const [selectedOpportunity, setSelectedOpportunity] = useState<PipelineOpportunity | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [opportunityToTransform, setOpportunityToTransform] =
    useState<PipelineOpportunity | null>(null);
  const [opportunityToMarkLost, setOpportunityToMarkLost] =
    useState<PipelineOpportunity | null>(null);
  const [opportunityForActivity, setOpportunityForActivity] =
    useState<PipelineOpportunity | null>(null);

  // Activity creation mutation (dynamic account/opportunity)
  const createActivityMutation = useMutation({
    mutationFn: async ({
      accountId,
      opportunityId,
      input,
    }: {
      accountId: string;
      opportunityId: string;
      input: CreateCrmActivityInput;
    }) => createActivity(accountId, opportunityId, input),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.byOpportunity(variables.accountId, variables.opportunityId),
      });
      queryClient.invalidateQueries({ queryKey: crmActivityKeys.upcoming() });
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.all });
      toast.success('Activity created', { description: result.title });
    },
    onError: (error) => {
      toast.error('Failed to create activity', {
        description: getErrorMessage(error),
      });
    },
  });

  // Handlers
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: crmPipelineKeys.board() });
  }, [queryClient]);

  const handleCreateOpportunity = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleConfirmCreate = useCallback(
    async (input: CreateOpportunityInput) => {
      try {
        await createOpportunityMutation.mutateAsync(input);
        setShowCreateModal(false);
      } catch {
        // Error toast already shown by mutation's onError
      }
    },
    [createOpportunityMutation]
  );

  const handleViewOpportunity = useCallback(
    (opportunity: PipelineOpportunity) => {
      navigate(`/crm/opportunities/${opportunity.id}`);
    },
    [navigate]
  );

  const handleAddActivity = useCallback(
    (opportunity: PipelineOpportunity) => {
      // Open the activity modal
      setOpportunityForActivity(opportunity);
    },
    []
  );

  const handleSaveActivity = useCallback(
    async (input: CreateCrmActivityInput) => {
      if (!opportunityForActivity) return;

      try {
        await createActivityMutation.mutateAsync({
          accountId: opportunityForActivity.account_id,
          opportunityId: opportunityForActivity.id,
          input,
        });
        setOpportunityForActivity(null);
      } catch {
        // Error toast already shown by mutation's onError
      }
    },
    [opportunityForActivity, createActivityMutation]
  );

  const handleQuoteTransform = useCallback((opportunity: PipelineOpportunity) => {
    setOpportunityToTransform(opportunity);
  }, []);

  const handleConfirmTransform = useCallback(
    async (projectName?: string) => {
      if (!opportunityToTransform) return;

      try {
        const result = await transformToQuoteMutation.mutateAsync({
          accountId: opportunityToTransform.account_id,
          opportunityId: opportunityToTransform.id,
          input: { project_name: projectName },
        });

        // Close modal and navigate to quotes
        setOpportunityToTransform(null);
        // Navigate to account quotes tab
        navigate(
          `/accounts/${opportunityToTransform.account_id}?tab=quotes&highlight=${result.quote_id}`
        );
      } catch (err) {
        console.error('Failed to transform to quote:', err);
      }
    },
    [opportunityToTransform, transformToQuoteMutation, navigate]
  );

  const handleMarkLost = useCallback((opportunity: PipelineOpportunity) => {
    setOpportunityToMarkLost(opportunity);
  }, []);

  const handleConfirmLost = useCallback(
    async (reason?: string) => {
      if (!opportunityToMarkLost) return;

      try {
        await markLostMutation.mutateAsync({
          accountId: opportunityToMarkLost.account_id,
          opportunityId: opportunityToMarkLost.id,
          reason,
        });
        setOpportunityToMarkLost(null);
      } catch (err) {
        console.error('Failed to mark as lost:', err);
      }
    },
    [opportunityToMarkLost, markLostMutation]
  );

  const handleLoadMore = useCallback(
    async (stageStr: string) => {
      const stage = stageStr as PipelineStage;
      if (!board) return;

      // Synchronous ref guard prevents double-clicks in the same render tick
      if (loadingStageRef.current === stage) return;

      // Find the current column to get the offset
      const column = board.columns.find((c) => c.key === stage);
      if (!column || !column.hasMore) return;

      loadingStageRef.current = stage;
      setLoadingStage(stage);
      try {
        await loadMoreMutation.mutateAsync({
          stage,
          offset: column.loadedCount,
        });
      } finally {
        loadingStageRef.current = null;
        setLoadingStage(null);
      }
    },
    [board, loadMoreMutation]
  );

  // Loading state
  if (isLoading) {
    return <CrmBoardSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="text-red-500 text-lg font-medium mb-2">Failed to load pipeline</div>
        <div className="text-gray-500 text-sm mb-4">{(error as Error).message}</div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty state
  if (!board || board.columns.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <CrmBoardHeader
          isLoading={isLoading}
          onRefresh={handleRefresh}
          onCreateOpportunity={handleCreateOpportunity}
          onToggleFilters={() => setShowFilters(!showFilters)}
          hasActiveFilters={hasActiveFilters}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-lg mb-2">No opportunities yet</div>
            <div className="text-gray-300 text-sm">
              Create your first opportunity to start tracking your sales pipeline
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Pipeline Tabs */}
      <div className="px-4 pt-2">
        <Tabs value={activePipeline} onValueChange={handlePipelineChange}>
          <TabsList>
            {PIPELINES.map((p) => (
              <TabsTrigger key={p.key} value={p.key}>
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Header */}
      <CrmBoardHeader
        summary={board.summary}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onCreateOpportunity={handleCreateOpportunity}
        onToggleFilters={() => setShowFilters(!showFilters)}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Filters */}
      <CrmFilterBar
        filters={filters}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        isVisible={showFilters}
      />

      {/* Board */}
      <CrmPipelineBoard
        board={board}
        onViewOpportunity={handleViewOpportunity}
        onAddActivity={handleAddActivity}
        onMarkLost={handleMarkLost}
        onQuoteTransform={handleQuoteTransform}
        onLoadMore={handleLoadMore}
        loadingStage={loadingStage}
      />

      {/* Create Opportunity Modal */}
      <CreateOpportunityModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onConfirm={handleConfirmCreate}
        isLoading={createOpportunityMutation.isPending}
      />

      {/* Transform to Quote Modal */}
      <TransformToQuoteModal
        open={!!opportunityToTransform}
        onOpenChange={(open) => !open && setOpportunityToTransform(null)}
        opportunity={opportunityToTransform}
        onConfirm={handleConfirmTransform}
        isLoading={transformToQuoteMutation.isPending}
      />

      {/* Mark Lost Modal */}
      <MarkLostModal
        open={!!opportunityToMarkLost}
        onOpenChange={(open) => !open && setOpportunityToMarkLost(null)}
        opportunity={opportunityToMarkLost}
        onConfirm={handleConfirmLost}
        isLoading={markLostMutation.isPending}
      />

      {/* Add Activity Modal */}
      <AddActivityModal
        open={!!opportunityForActivity}
        onOpenChange={(open) => !open && setOpportunityForActivity(null)}
        onSave={handleSaveActivity}
        isSaving={createActivityMutation.isPending}
      />

      {/* Opportunity Detail Sheet */}
      <Sheet open={!!selectedOpportunity} onOpenChange={(open) => { if (!open) setSelectedOpportunity(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
          {selectedOpportunity && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base leading-snug pr-4">{selectedOpportunity.title}</SheetTitle>
                <p className="text-sm text-muted-foreground">
                  {selectedOpportunity.account_name || 'Prospect'}
                  {selectedOpportunity.account_number && ` (${selectedOpportunity.account_number})`}
                </p>
              </SheetHeader>

              <div className="flex-1 mt-6 space-y-5 overflow-y-auto">
                {/* Stage + Type + Priority */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge style={{ backgroundColor: pipelineDef.stages.find((s) => s.key === selectedOpportunity.stage)?.probability ? pipelineDef.color : '#6366f1' }}>
                    {pipelineDef.stages.find((s) => s.key === selectedOpportunity.stage)?.label || selectedOpportunity.stage}
                  </Badge>
                  <Badge variant="outline" className="text-xs">{selectedOpportunity.type}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{selectedOpportunity.priority || 'medium'}</Badge>
                  {(selectedOpportunity.days_in_stage ?? 0) > 0 && (
                    <span className="text-xs text-muted-foreground">{selectedOpportunity.days_in_stage}d in stage</span>
                  )}
                </div>

                {/* Value + Probability */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedOpportunity.estimated_value != null && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Estimated Value</span>
                      <span className="font-semibold">{formatCurrency(selectedOpportunity.estimated_value, selectedOpportunity.currency)}</span>
                    </div>
                  )}
                  {selectedOpportunity.probability != null && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Probability</span>
                      <span className="font-semibold">{selectedOpportunity.probability}%</span>
                    </div>
                  )}
                  {selectedOpportunity.target_close_date && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Target Close</span>
                      <span>{selectedOpportunity.target_close_date.substring(0, 10)}</span>
                    </div>
                  )}
                  {selectedOpportunity.stage_entered_at && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Stage Since</span>
                      <span>{new Date(selectedOpportunity.stage_entered_at).toLocaleDateString('en-CA')}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                {selectedOpportunity.description && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Description</span>
                    <p className="text-sm whitespace-pre-wrap">{selectedOpportunity.description}</p>
                  </div>
                )}

                {/* Tender source info */}
                {selectedOpportunity.type === 'tender' && (selectedOpportunity as any).signalData && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Tender Source</span>
                    <div className="text-sm space-y-1">
                      {(selectedOpportunity as any).signalData.source && (
                        <p>Source: <Badge variant="outline" className="text-xs">{(selectedOpportunity as any).signalData.source}</Badge></p>
                      )}
                      {(selectedOpportunity as any).signalData.solicitationNumber && (
                        <p>Solicitation: <span className="font-mono text-xs">{(selectedOpportunity as any).signalData.solicitationNumber}</span></p>
                      )}
                      {(selectedOpportunity as any).signalData.confidenceScore != null && (
                        <p>Confidence Score: <span className="font-semibold">{(selectedOpportunity as any).signalData.confidenceScore}/100</span></p>
                      )}
                      {(selectedOpportunity as any).signalData.url && (
                        <a href={(selectedOpportunity as any).signalData.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs">
                          <ExternalLink className="h-3 w-3" /> View original tender
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t flex gap-2">
                {selectedOpportunity.account_id && (
                  <button
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => { setSelectedOpportunity(null); navigate(`/accounts/${selectedOpportunity.account_id}?tab=opportunities`); }}
                  >
                    View Account
                  </button>
                )}
                <button className="text-sm text-muted-foreground hover:text-foreground ml-auto" onClick={() => setSelectedOpportunity(null)}>
                  Close
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
